import React, { ComponentPropsWithoutRef, FunctionComponent } from 'react';
import { joinStrings } from '../../../utils/string';
import {
  PropValidationRuleConfig,
  PropValidationRuleLogType,
  withPropValidation,
} from '../../../utils/withPropValidation/withPropValidation';
import { formatCurrency } from '../../../utils/wallets';
import ClockIcon from '../../../assets/Itv/clock.svg';
import './styles';

export type ColorThreshold = {
  value: number;
  color: string;
  textColor: string | null | undefined;
};

export type ClassThreshold = {
  value: number;
  className: string;
};

export type ProgressBarTypes = 'primary' | 'accent' | 'success' | 'warning' | 'danger';

export type ProgressBarProps = {
  /*
   * Determines the color of the progress bar.
   */
  type?: ProgressBarTypes;
  /**
   * Used to set the aria-valuemin and calculate progress percentage.  Defauts to 0.
   */
  min?: number;
  /**
   * Used to set the aria-valuemax and calculate progress percentage.  Defauts to 100.
   */
  max?: number;
  /**
   * The current width of the progress bar, calculated with the min and max values in mind.  For example, if the min is 0 and the max is 20, then a value of 10 will be 50%.
   */
  value?: number;
  /**
   * Show the current value value.  Due to spacing, will only show if the value is above 10.
   */
  showValue?: boolean;
  /**
   * Show ratio match info values to the right - how much is spent and how much is left to a maximum amount
   */
  showWageringInfo?: boolean;
  /**
   * Can add a class (or classes) to the uppermost level of the component.
   */
  className?: string;
  /**
   * Allows the use of custom colors that are triggered upon hitting a certain percentage.  Can alter the progressbar color and text color properties.
   */
  colorThreshold?: ColorThreshold[];
  /**
   * Allows the use of custom classes that are triggered upon hitting a certain percentage.
   */
  classThreshold?: ClassThreshold[];
  /**
   * Determines show/hide currency symbol
   */
  showCurrency?: boolean;
} & ComponentPropsWithoutRef<'div'>;

type ProgressBarType = {
  width: string;
  backgroundColor?: string;
  color?: string;
};
/**
 * Functions
 */
export const getCurrentProgressBar = ({
  value,
  colorThreshold,
}: {
  value: number | null;
  colorThreshold: ColorThreshold[];
}): ProgressBarType => {
  const item =
    colorThreshold.find((x: ColorThreshold) => {
      return value ? value <= x.value : null;
    }) || null;

  return item
    ? {
        width: `${value}%`,
        backgroundColor: item.color,
        color: item.textColor || `#fff`,
      }
    : { width: `${value || 0}%` };
};

/**
 * Returns the percentage of a value with with a min, max, and current value
 */
export function rangePercentage(value: number, min: number, max: number): number {
  const val = ((value - min) * 100) / (max - min);

  return val < 99 ? Math.ceil(val) : Math.floor(val);
}

export const getClassThresholds = ({
  value,
  classThreshold,
}: {
  value: number;
  classThreshold: ClassThreshold[];
}): string | null => {
  const item =
    classThreshold.find((x: ClassThreshold) => {
      return value ? value <= x.value : null;
    }) || null;

  return item ? `progress-bar__inner-bar--${item.className}` : null;
};

/**
 A simple ProgressBar component

  ### Simple Example
  ```tsx
  <ProgressBar value={50} />
  ```
 */

const Component: FunctionComponent<ProgressBarProps> = ({
  value = 0,
  min = 0,
  max = 100,
  type = 'primary',
  showCurrency = true,
  showValue,
  showWageringInfo,
  className,
  colorThreshold = [],
  classThreshold = [],
  ...props
}) => (
  <div className="progress-bar">
    <div
      {...props}
      role="progressbar"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      className={joinStrings(['progress-bar__inner-wrapper', className])}
    >
      <div className="progress-bar__inner">
        <div
          className={joinStrings([
            `progress-bar__inner-bar`,
            `progress-bar__inner-bar--${type}`,
            getClassThresholds({
              value: rangePercentage(value, min, max),
              classThreshold,
            }),
          ])}
          style={getCurrentProgressBar({
            value: rangePercentage(value, min, max),
            colorThreshold,
          })}
        >
          {showValue && rangePercentage(value, min, max) > 10 ? (
            <div className="progress-bar__inner-text">{rangePercentage(value, min, max)}%</div>
          ) : null}
        </div>
      </div>
    </div>
    {showWageringInfo && (
      <div>
        <div className="progress-bar__values">
          <img className="progress-bar__clock-icon" src={ClockIcon} alt="clock-icon" />
          <div>
            <span className="progress-bar__wagered">
              {showCurrency ? formatCurrency(value, 'auto', 'auto') : value}
            </span>
            <span className="progress-bar__devider">/</span>
            <span className="progress-bar__assumed">{showCurrency ? formatCurrency(max, 'auto', 'auto') : max}</span>
          </div>
        </div>
      </div>
    )}
  </div>
);

Component.displayName = 'ProgressBar';

const logType: PropValidationRuleLogType = 'error';
const validationRules: PropValidationRuleConfig<ProgressBarProps>[] = [
  {
    message: 'Min value should not exceed max value - please double check props.',
    logType,
    validate: ({ min, max }: ProgressBarProps): boolean => {
      return min && max ? min >= max : false;
    },
  },
];

export const ProgressBar = withPropValidation({ Component, validationRules });
